/**
 * Full IANA timezone list for the agent timezone picker. Sourced at runtime
 * from the browser's own timezone database (Intl.supportedValuesOf) rather
 * than a hand-maintained per-country subset — this is deliberate so the list
 * doesn't need to be updated every time SuzukiDesk adds a new country; any
 * IANA zone is selectable from day one, mirroring backend/src/common/utils/timezones.ts.
 */
const FALLBACK_TIMEZONES = [
  'UTC',
  'Asia/Kolkata',
  'Asia/Tokyo',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Dubai',
  'Europe/Bucharest',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Los_Angeles',
  'Australia/Sydney',
];

function loadIanaTimezones(): string[] {
  try {
    const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.('timeZone');
    if (supported && supported.length > 0) {
      return supported.includes('UTC') ? supported : ['UTC', ...supported];
    }
  } catch {
    // fall through to static fallback
  }
  return FALLBACK_TIMEZONES;
}

/** Full list of valid IANA timezone identifiers, e.g. "Asia/Kolkata". */
export const IANA_TIMEZONES: readonly string[] = loadIanaTimezones();

/** `{ value, label }` options for a DropdownSelect/SearchableSelect, sorted alphabetically. */
export const TIMEZONE_OPTIONS: { value: string; label: string }[] = [...IANA_TIMEZONES]
  .sort((a, b) => a.localeCompare(b))
  .map((zone) => ({ value: zone, label: zone }));

/** The browser's own detected timezone, used as a sensible default when creating a new agent. */
export function detectBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}
